const STEP_SIZE = Number(import.meta.env.VITE_CONTRIBUTION_STEP_VALUE) || 5;
const MINIMUM_AMOUNT =
  Number(import.meta.env.VITE_MINIMUM_CONTRIBUTION_AMOUNT) || 10;
const MAXIMUM_AMOUNT =
  Number(import.meta.env.VITE_MAXIMUM_CONTRIBUTION_AMOUNT) || 9000;
const DEFAULT_AMOUNT =
  Number(import.meta.env.VITE_DEFAULT_CONTRIBUTION_AMOUNT) || 50;
const PRESET_AMOUNTS = Object.freeze([15, 25, 50]);

const DEFAULT_LOCALE = import.meta.env.VITE_DEFAULT_LOCALE || 'en-GB';
const DEFAULT_CURRENCY = import.meta.env.VITE_DEFAULT_CURRENCY || 'GBP';

/**
 * Character cap on the written message (GIFTWEB-25 AC 2). The env var keeps
 * its original name — the occasion field itself was dropped in V2.
 */
const MESSAGE_MAX_LENGTH =
  Number(import.meta.env.VITE_OCCASION_MESSAGE_MAX_LENGTH) || 300;

/** Recording caps, in seconds (GIFTWEB-21 AC 1 — 30s for both). */
const AUDIO_TIME_LIMIT_SECONDS =
  Number(import.meta.env.VITE_AUDIO_TIME_LIMIT_SECONDS) || 30;
const VIDEO_TIME_LIMIT_SECONDS =
  Number(import.meta.env.VITE_VIDEO_TIME_LIMIT_SECONDS) || 30;

const DEBOUNCE_DELAY_MS = Number(import.meta.env.VITE_DEBOUNCE_DELAY_MS) || 50; 


const MilestoneTypes = Object.freeze({
  university: 'university',
  home: 'home',
  gap: 'gap',
  car: 'car',
  holiday: 'holiday',
  other: 'other',
});

const MileStoneTitleMap = Object.freeze({
  [MilestoneTypes.university]: 'University Fees',
  [MilestoneTypes.home]: 'House Deposit',
  [MilestoneTypes.gap]: 'Gap Year',
  [MilestoneTypes.car]: 'First Car',
  [MilestoneTypes.holiday]: 'Holiday',
});

const storageKeys = Object.freeze({
  audioBlob: 'audioBlob',
  videoBlob: 'videoBlob',
  profilePhotoBlob: 'profilePhotoBlob',
  messagePhotoBlob: 'messagePhotoBlob',
});

/**
 * Photo attached to a written message (GIFTWEB-22). This is the message
 * photo, not the contributor's profile photo — different endpoint, and the
 * V2 design drops the profile one from the details step.
 */
const PHOTO_MAX_BYTES =
  Number(import.meta.env.VITE_MV_PHOTO_MAX_BYTES) || 10 * 1024 * 1024;

const PHOTO_MIME_TYPES = Object.freeze([
  'image/jpeg',
  'image/jpg',
  'image/png',
]);

/**
 * Relationship options offered on the details step, in display order. Values
 * are the API enum for `relationshipToChild` (PUT …/details); the order
 * follows the V2 design (most likely gifters first). The design also offers
 * Sibling, a merged "Aunt / Uncle" and Other — those wait on the backend
 * "extend the relationship enum" ticket (GIFTWEB-19).
 */
const RELATIONSHIP_OPTIONS = Object.freeze([
  { value: 'parent', label: 'Parent' },
  { value: 'grandparent', label: 'Grandparent' },
  { value: 'aunt', label: 'Aunt' },
  { value: 'uncle', label: 'Uncle' },
  { value: 'godparent', label: 'Godparent' },
  { value: 'family-friend', label: 'Family friend' },
  { value: 'friend', label: 'Friend' },
  { value: 'cousin', label: 'Cousin' },
  { value: 'niece', label: 'Niece' },
  { value: 'nephew', label: 'Nephew' },
] as const);

type RelationshipValue = (typeof RELATIONSHIP_OPTIONS)[number]['value'];

/** value → label map, kept for callers that want a lookup. */
const relationshipOptions = Object.freeze(
  Object.fromEntries(RELATIONSHIP_OPTIONS.map((o) => [o.value, o.label])),
) as Readonly<Record<RelationshipValue, string>>;

const openBankingStatuses = Object.freeze({
  succeeded: 'succeeded',
  failed: 'failed',
  cancelled: 'cancelled',
  expired: 'expired',
  created: 'created',
  pending: 'pending',
});

/** Statuses the API will not move away from — polling stops on any of them. */
const TERMINAL_OPEN_BANKING_STATUSES: readonly string[] = Object.freeze([
  openBankingStatuses.succeeded,
  openBankingStatuses.failed,
  openBankingStatuses.cancelled,
  openBankingStatuses.expired,
]);

const isTerminalOpenBankingStatus = (status?: string | null): boolean =>
  !!status && TERMINAL_OPEN_BANKING_STATUSES.includes(status);

/**
 * Open Banking status polling (GIFTWEB-26 AC 3 and AC 8). The interval is the
 * gap between polls; the window is how long we keep asking before showing the
 * still-processing screen. Jira left the window open ("Polling stops after 3
 * attempts today, which is about 6 seconds… agree the number before build") —
 * 90s was agreed: long enough for a bank round-trip including 2FA, short
 * enough not to strand anyone on a spinner.
 */
const PAYMENT_POLL_INTERVAL_MS =
  Number(import.meta.env.VITE_PAYMENT_POLL_INTERVAL_MS) || 2_000;

const PAYMENT_POLL_WINDOW_MS =
  Number(import.meta.env.VITE_PAYMENT_POLL_WINDOW_MS) || 90_000;

/**
 * TrueLayer's own signal that the contributor walked away from the hosted
 * payment page. We still trust our API for the real status, but sitting on a
 * spinner for the full window when TrueLayer has already told us they left is
 * pointless — so a short grace period is given for a webhook to land, and then
 * the cancelled outcome is shown (GIFTWEB-26 AC 6).
 */
const TRUELAYER_ABANDONED_ERROR = 'tl_hpp_abandoned';

const PAYMENT_ABANDONED_GRACE_MS =
  Number(import.meta.env.VITE_PAYMENT_ABANDONED_GRACE_MS) || 10_000;

/**
 * Where to send someone to install the Mia app (GIFTWEB-27, GIFTWEB-30).
 *
 * PROVISIONAL — GIFTWEB-31 has to settle two things before this ships:
 *
 * 1. The App Store identifier. Three are in use across the estate and at most
 *    one is right: `6749765237` in the app's own constants, `6755721245` here,
 *    `6504461457` in the API. The default below is this repo's existing value,
 *    carried over rather than endorsed. Someone with App Store Connect access
 *    has to confirm the live one.
 * 2. The AppsFlyer OneLink. Until it exists there is no single URL that routes
 *    per device, so the smart link falls back to the website — which is a
 *    sensible landing page, not the store listing the design asks for.
 *
 * All three are env-overridable so settling them is a configuration change in
 * one place, in line with GIFTWEB-31 AC 6.
 */
const APP_STORE_URL =
  import.meta.env.VITE_APP_STORE_URL ||
  'https://apps.apple.com/gb/app/mia-wealth/id6755721245';

const PLAY_STORE_URL =
  import.meta.env.VITE_PLAY_STORE_URL ||
  'https://play.google.com/store/apps/details?id=com.miawealth.miawealth';

/**
 * How long before a signed photo URL dies that we go and get a new one.
 *
 * The expiry is the server's, so a client clock a little out of step could
 * think a dead signature is still good; and a signature that expires mid-flight
 * is no use either. A minute covers both.
 */
const PHOTO_EXPIRY_MARGIN_MS =
  Number(import.meta.env.VITE_PHOTO_EXPIRY_MARGIN_MS) || 60_000;

const APP_SMART_LINK_URL =
  import.meta.env.VITE_APP_SMART_LINK_URL || 'https://www.miawealth.co.uk';

export type { RelationshipValue };

export {
  MilestoneTypes,
  MileStoneTitleMap,
  storageKeys,
  relationshipOptions,
  RELATIONSHIP_OPTIONS,
  openBankingStatuses,
  TERMINAL_OPEN_BANKING_STATUSES,
  isTerminalOpenBankingStatus,
  PAYMENT_POLL_INTERVAL_MS,
  PAYMENT_POLL_WINDOW_MS,
  PAYMENT_ABANDONED_GRACE_MS,
  TRUELAYER_ABANDONED_ERROR,
  APP_STORE_URL,
  PLAY_STORE_URL,
  APP_SMART_LINK_URL,
  PHOTO_EXPIRY_MARGIN_MS,
  STEP_SIZE,
  MINIMUM_AMOUNT,
  MAXIMUM_AMOUNT,
  DEFAULT_AMOUNT,
  PRESET_AMOUNTS,
  DEFAULT_LOCALE,
  DEFAULT_CURRENCY,
  DEBOUNCE_DELAY_MS,
  MESSAGE_MAX_LENGTH,
  AUDIO_TIME_LIMIT_SECONDS,
  VIDEO_TIME_LIMIT_SECONDS,
  PHOTO_MAX_BYTES,
  PHOTO_MIME_TYPES,
};
